/**
 * Tracking guard for the headless GoodnessMe storefront.
 *
 * Why: local dev environments must not emit events to production Klaviyo,
 * Elevar, GA4, Facebook, TikTok or Google Ads. Elevar Support flagged
 * `localhost` URLs leaking into production data on 20 Apr 2026.
 *
 * Behaviour: tracking fires everywhere EXCEPT local-dev hostnames and
 * automated browsers. Netlify deploy previews, staging, and production all
 * track normally with no env configuration required. Block list is sourced
 * from GATSBY_TRACKING_BLOCKED_HOSTS (comma-separated, optional) and defaults
 * to the common localhost aliases.
 */

const DEFAULT_BLOCKED_HOSTS = ["localhost", "127.0.0.1", "0.0.0.0", "::1"]

const blockedHosts = (
  process.env.GATSBY_TRACKING_BLOCKED_HOSTS?.split(",")
    .map(host => host.trim())
    .filter(Boolean) ?? DEFAULT_BLOCKED_HOSTS
).map(host => host.toLowerCase())

const AUTOMATED_UA_PATTERN =
  /headless|lighthouse|phantomjs|selenium|puppeteer|playwright|crawler|spider|scraper|googlebot|adsbot|bingbot|slurp|duckduckbot|baiduspider|yandexbot|petalbot|bytespider|gptbot|claudebot|ccbot|amazonbot|facebookexternalhit|bingpreview|semrush|ahrefs/i

/**
 * Conservative automated-browser check: only unambiguous signals, since a
 * false positive hides a real shopper from every analytics destination.
 */
const isAutomatedBrowser = (): boolean => {
  if (typeof window === "undefined") return false
  return window.navigator?.webdriver === true || AUTOMATED_UA_PATTERN.test(window.navigator?.userAgent ?? "")
}

export const isTrackingAllowed = (): boolean => {
  if (typeof window === "undefined") return false
  if (isAutomatedBrowser()) return false
  const hostname = window.location?.hostname?.toLowerCase()
  if (!hostname) return false
  return !blockedHosts.includes(hostname)
}

/**
 * Per-event unique id for FB Conversion API / TikTok pixel server-side dedupe.
 * Consumed by the GTM container variable `dlv - event_id`. Injected
 * automatically by pushElevarDataLayer so every dl_* push carries one.
 */
export const generateEventId = (): string => {
  if (typeof window === "undefined") return ""
  if (typeof window.crypto?.randomUUID === "function") return window.crypto.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}
